import React, { forwardRef, memo, useEffect, useState } from "react"
import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  HStack,
  IconButton,
  ListItem,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
  UnorderedList,
  useDisclosure,
} from "@chakra-ui/react"
import { Icon } from "@app/components/Icon"
import { ReviewCard } from "@app/components/Product/Review/ReviewCard"
import { FormButton } from "@app/components/Form/FormButton"
import { ReviewOverview } from "./ReviewOverview"
import { useFunctions } from "@app/hooks/useFunctions"
import { IReview, IReviewDataResponse } from "./ReviewTypes"
import { FormInput } from "@app/components/Form/FormInput"
import { useForm } from "@app/hooks/useForm"
import { Loader } from "@app/components/Loader/Loader"
import { graphql, useStaticQuery } from "gatsby"
import useScrollbarSize from "react-scrollbar-size"
import ReviewAddDrawer from "@app/components/Product/Review/Add/ReviewAddDrawer"

import type { ProductCombinedProps, ProductProps } from "@root/types/global"

const sortOptions = [
  { label: "Newest", value: "new" },
  { label: "Oldest", value: "old" },
  { label: "Highest Rated", value: "high" },
  { label: "Lowest Rated", value: "low" },
]

type Props = {
  product: ProductCombinedProps
  ref?: React.MutableRefObject<null>
  swatches?: Array<ProductProps>
}

export const ReviewSection: React.FC<Props> = memo(
  forwardRef(({ product, swatches }, ref) => {
    const { locales } = useStaticQuery<GatsbyTypes.ReviewSectionQuery>(graphql`
      query ReviewSection {
        locales: sanityTemplateProduct {
          additionalNoReviewsText
          additionalWriteReviewsText
          additionalReviewsLoadMoreText
          additionalReviewsSortText
          additionalReviewsSectionTextTitle
        }
      }
    `)

    const INITIAL_STATE = {
      search: "",
    }
    const { data, handleChange, handleReset } = useForm(INITIAL_STATE)

    const formatDateForDisplay = (dateString: string) => {
      const date = new Date(dateString)
      if (!date || typeof date === "string") return ""
      const year = date.getFullYear()
      const month = ("0" + (date.getMonth() + 1)).slice(-2)
      const day = ("0" + date.getDate()).slice(-2)
      return `${day}.${month}.${year}`
    }

    const mapReviewData = (response: any) => {
      return response
        ? {
            averageScore: response.bottomline?.average_score,
            totalReviews: response.bottomline?.total_review,
            reviews: response.reviews.map(review => ({
              author: review.user.display_name,
              contentMessage: review.content,
              contentTitle: review.title,
              createdDate: new Date(review.created_at).getUTCDate(),
              displayDate: formatDateForDisplay(review.created_at),
              score: review.score,
              id: review.id,
            })),
          }
        : null
    }

    const { width } = useScrollbarSize()

    const { isOpen, onOpen, onClose } = useDisclosure()
    const btnRef = React.useRef<HTMLButtonElement>(null)

    const { callFunction } = useFunctions()
    const [currentVisibleReviewCount, setCurrentVisibleReviewCount] = useState(4)
    const [reviewData, setReviewData] = useState<IReviewDataResponse>()
    const [reviews, setReviews] = useState<IReview[]>()
    const [isLoadingReviews, setIsLoadingReviews] = useState<boolean>(false)
    const [isSorted, setIsSorted] = useState<boolean>(false)

    const getReviewsFunction = () => {
      if (!product?.legacyId) return
      setIsLoadingReviews(true)
      const fetchReviews = async () => {
        const {
          body: { response: reviewsResponse },
        } = await callFunction("reviews", { productId: product?.legacyId })

        const mappedReviewsResponse = mapReviewData(reviewsResponse) as IReviewDataResponse
        setReviewData(mappedReviewsResponse)
        setReviews(mappedReviewsResponse.reviews)
        setIsLoadingReviews(false)
      }
      fetchReviews()
    }

    useEffect(() => {
      getReviewsFunction()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    //Parse date string to date object.

    //Update reviews sort.
    const sortReviews = (sortType: string) => {
      switch (sortType) {
        case "new":
          reviews?.sort((a, b) => b.createdDate - a.createdDate)
          break
        case "old":
          reviews?.sort((a, b) => a.createdDate - b.createdDate)
          break
        case "high":
          reviews?.sort((a, b) => b.score - a.score)
          break
        case "low":
          reviews?.sort((a, b) => a.score - b.score)
          break
        default:
          break
      }
    }

    //This handles the search functionality.
    const handleSearch = async (e: React.BaseSyntheticEvent) => {
      e.preventDefault()
      setIsLoadingReviews(true)
      const searchTerm = e.target["search"].value
      if (searchTerm === "") {
        getReviewsFunction()
        setIsSorted(false)
      } else {
        const {
          body: { response },
        } = await callFunction("search-reviews", { body: { domain_key: product?.legacyId, free_text_search: searchTerm } })
        if (response.found_filtered_reviews) {
          const mappedReviews = mapReviewData(response)
          setReviews(mappedReviews?.reviews)
        } else {
          setReviews([])
        }
        setIsSorted(true)
      }

      setIsLoadingReviews(false)
    }

    const resetSearchForm = () => {
      handleReset()
      if (isSorted) {
        getReviewsFunction()
        setIsSorted(false)
      }
    }

    return (
      <>
        <Container
          px={0}
          display={"block"}
          mx={{ base: 6, sm: "10.5rem" }}
          my={{ base: 14, sm: 14 }}
          id="review"
          ref={ref}
          scrollMargin={"70"}
          width={"auto"}
        >
          <Container display={"flex"} alignContent={"center"} flexDirection={"column"} width={"fit-content"} gap={4} px="0">
            <Text fontSize={["30px", "36px"]} as="h2" fontWeight={500} lineHeight={1.2} color="grey.text" textAlign="center">
              {locales?.additionalReviewsSectionTextTitle}
            </Text>
            {reviews?.length === 0 && (
              <Flex justifyContent={"center"}>
                <Text fontSize={["lg", "xl"]} lineHeight={1.2} fontWeight={500} color="grey.text" textAlign="center">
                  {locales?.additionalNoReviewsText}
                </Text>
              </Flex>
            )}
            {reviews && (
              <Box margin={"auto"}>
                <ReviewOverview totalReviews={reviewData?.totalReviews || 0} averageReview={reviewData?.averageScore} />
              </Box>
            )}
            <Button variant="review" w={"fit-content"} mx="auto" ref={btnRef} colorScheme="teal" color="grey.coal" onClick={onOpen} mb={8}>
              {locales?.additionalWriteReviewsText}
            </Button>
            <ReviewAddDrawer isOpen={isOpen} onClose={onClose} product={product} width={width} swatches={swatches} />
          </Container>

          {reviewData?.totalReviews > 0 && (
            <Flex flexDirection={{ base: "column", sm: "row" }} width={"100%"} gap={{ base: 0, sm: 8 }} as="form" onSubmit={handleSearch}>
              <FormControl flexGrow={1}>
                <FormInput
                  autoComplete="off"
                  letterSpacing="broad"
                  name="search"
                  id="search"
                  placeholder={"SEARCH"}
                  onChange={handleChange}
                  value={data?.search}
                  rightAddon={
                    <HStack alignItems="center" justifyContent="flex-end" pb={2} spacing={1}>
                      {data?.search || isSorted ? (
                        <IconButton
                          display="block"
                          icon={<Icon name="close" width={5} />}
                          title={"additionalClear"}
                          aria-label={"additionalClear"}
                          variant="ghost"
                          onClick={resetSearchForm}
                        />
                      ) : null}
                      <IconButton
                        display="block"
                        icon={<Icon name="search" width={4} />}
                        title={"placeholder"}
                        aria-label={"search"}
                        type="submit"
                        color={"grey.darker"}
                        variant="ghost"
                      />
                    </HStack>
                  }
                  variant="minimal"
                />
              </FormControl>
              <Flex marginTop={{ base: 8, md: 0 }} justifyContent="center">
                <Menu>
                  <MenuButton as={Button} variant="review">
                    Sort
                  </MenuButton>
                  <MenuList>
                    <MenuOptionGroup defaultValue={"new"} title="Sort" type="radio" onChange={value => sortReviews(value)}>
                      {sortOptions.map(option => (
                        <MenuItemOption key={option.value} value={option.value}>
                          {option.label}
                        </MenuItemOption>
                      ))}
                    </MenuOptionGroup>
                  </MenuList>
                </Menu>
              </Flex>
            </Flex>
          )}

          {!isLoadingReviews ? (
            reviews &&
            reviews.length > 0 && (
              <UnorderedList styleType={"none"} marginInlineStart={0}>
                {reviews?.map((review, i) => {
                  if (i < currentVisibleReviewCount) {
                    return (
                      <ListItem key={review.id} py={8} borderBottom={"1px"} borderColor={"grey.mid"} marginInlineStart={0}>
                        <ReviewCard review={review} />
                      </ListItem>
                    )
                  }
                })}
              </UnorderedList>
            )
          ) : (
            <Loader></Loader>
          )}
          {reviews && reviews?.length > currentVisibleReviewCount && (
            <Box textAlign={"center"}>
              <FormButton
                mt={10}
                mx={"auto"}
                alignItems="center"
                variant="review"
                onClick={() => setCurrentVisibleReviewCount(currentVisibleReviewCount + 4)}
              >
                {locales?.additionalReviewsLoadMoreText}
              </FormButton>
            </Box>
          )}
        </Container>
      </>
    )
  })
)
